import { initialsFor, type Me } from '../../../../lib/me';
import { useProfilePhoto } from '../../../../lib/use-profile-photo';
import { useRecipient } from '../../../settings/shared/settings-queries';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useTranslation } from '../../../../lib/i18n';

export interface AccountSummary {
  /** Empty until `me` resolves — callers reserve the row's height rather than jumping. */
  initials: string;
  displayName: string;
  email: string;
  /** The profile photo, or null when absent / it failed to load. */
  photoUrl: string | null;
  onPhotoError: () => void;
  settings: { route: string; path: string; label: string; onClick: () => void };
}


// Who the account row shows and where it goes.
// Shared by the wide rail's account card and the mobile drawer's account row.
export function useAccountSummary(me: Me | null): AccountSummary {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const { data: recipient } = useRecipient();
  // Prefer the recipient's own self-photo (set in Settings) over the Wix identity photo, so the rail
  // card and the drawer row show the same self-photo the Account page does.
  const { photoUrl, onPhotoError } = useProfilePhoto(me, recipient?.avatarUrl);

  const fullName = me ? `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() : '';

  return {
    initials: initialsFor(me),
    displayName: fullName || t('sidebar.account.fallback'),
    email: me?.email || me?.userName || '',
    photoUrl,
    onPhotoError,
    settings: {
      route: '/settings',
      path: `${basePath}/settings`,
      label: t('nav.settings'),
      onClick: () => {
        markNavFromDrawer();
        logPageClickBi({ page_name: 'menu', button_name: 'drawer_settings', origin: 'menu' });
        leaveChat();
      },
    },
  };
}
